import { useRef, useState, type ChangeEvent } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { VButton } from "@/components/ui/vButton";
import { Caption, Body } from "@/components/ui/typography";
import { cn } from "@/components/ui/cn";

type VFileUploadFile = {
  id: string;
  name: string;
  size: number;
  previewUrl?: string;
};

type VFileUploadProps = {
  files: ReadonlyArray<VFileUploadFile>;
  onAdd: (files: ReadonlyArray<File>) => void;
  onRemove: (id: string) => void;
  accept?: string;
  multiple?: boolean;
  hint?: string;
  className?: string;
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function VFileUpload({
  files,
  onAdd,
  onRemove,
  accept = "image/png,image/jpeg,image/webp",
  multiple = true,
  hint = "PNG, JPG of WEBP — max 10MB per bestand",
  className,
}: VFileUploadProps): React.JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);

  function handleChange(e: ChangeEvent<HTMLInputElement>): void {
    const list = e.target.files;
    if (!list) return;
    onAdd(Array.from(list));
    e.target.value = "";
  }

  return (
    <div className={cn("space-y-3", className)}>
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onAdd(Array.from(e.dataTransfer.files));
        }}
        className={cn(
          "flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed px-6 py-8 transition-colors",
          dragOver ? "border-primary-500 bg-primary-50" : "border-border bg-muted/30",
        )}
      >
        <Upload className="size-6 text-muted-foreground" aria-hidden />
        <Body tone="muted">Sleep bestanden hierheen of</Body>
        <VButton
          type="button"
          variant="outline"
          size="sm"
          onClick={() => inputRef.current?.click()}
        >
          Selecteer bestanden
        </VButton>
        <Caption tone="muted">{hint}</Caption>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          className="hidden"
          onChange={handleChange}
        />
      </div>
      {files.length > 0 ? (
        <ul className="space-y-2">
          {files.map((f) => (
            <li
              key={f.id}
              className="flex items-center gap-3 rounded-md border border-border bg-card px-3 py-2"
            >
              <div className="flex size-10 items-center justify-center overflow-hidden rounded bg-muted">
                {f.previewUrl ? (
                  <img src={f.previewUrl} alt="" className="size-full object-cover" />
                ) : (
                  <ImageIcon className="size-4 text-muted-foreground" aria-hidden />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <Body weight="medium" className="truncate">
                  {f.name}
                </Body>
                <Caption tone="muted">{formatBytes(f.size)}</Caption>
              </div>
              <VButton
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Verwijder ${f.name}`}
                onClick={() => onRemove(f.id)}
                icon={<X className="size-4" aria-hidden />}
              />
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export type { VFileUploadFile, VFileUploadProps };
